import React from 'react';
import { CheckCircle, X, Zap, Leaf, Settings, Award, TrendingDown, Shield, Thermometer, Wind, Atom, Gauge, Activity } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { siteInfo } from '../data/mock';

const Technologie = () => {
  const scientificPrinciples = [
    {
      title: "Modification des propriétés comburantes",
      description: "L'air ambiant (78% azote, 21% oxygène, 1% gaz divers) est collecté, structuré et dynamisé dans un réacteur qui le transforme en 'super comburant' ou 'air augmenté'.",
      icon: Atom,
      color: "cyan"
    },
    {
      title: "Action sur l'oxygène",
      description: "Le système rend l'oxygène plus réactif (super oxydant), optimisant sa capacité à oxyder le carburant lors de la combustion.",
      icon: Wind,
      color: "blue"
    },
    {
      title: "Structuration de l'azote",
      description: "L'azote est restructuré pour réduire les pertes calorifiques et diminuer la formation de NOx (oxydes d'azote).",
      icon: Activity,
      color: "green"
    }
  ];

  const combustionEffects = [
    {
      title: "Combustion plus complète",
      description: "Réduction des hydrocarbures (HC) non brûlés grâce à une meilleure oxydation du carburant",
      value: "↓ HC imbrûlés"
    },
    {
      title: "Combustion plus froide",
      description: "Diminution de la température des gaz d'échappement, réduisant les pertes calorifiques",
      value: "-50°C à -150°C"
    },
    {
      title: "Meilleur rendement stœchiométrique",
      description: "Optimisation du rapport air-carburant pour une combustion idéale",
      value: "+15 à 35%"
    },
    {
      title: "Réduction multi-polluants",
      description: "Baisse simultanée de CO, CO₂, NOx, particules fines et opacité des gaz",
      value: "-40 à 80%"
    }
  ];

  const technicalData = [
    { label: "Température de combustion", before: "1800-2300°C", after: "1650-2100°C", improvement: "-150 à -200°C" },
    { label: "Température gaz d'échappement", before: "600-800°C", after: "500-700°C", improvement: "-100°C" },
    { label: "Opacité fumées (diesel)", before: "3-5 m⁻¹", after: "0.5-1 m⁻¹", improvement: "-80%" },
    { label: "Émissions NOx", before: "Variable", after: "Variable", improvement: "-40 à -60%" },
    { label: "Niveau sonore", before: "Référence", after: "Référence -5dB", improvement: "-5 décibels" },
    { label: "Consommation carburant", before: "100%", after: "65-85%", improvement: "-15 à -35%" }
  ];

  const reactionSteps = [
    {
      step: 1,
      title: "Aspiration de l'air ambiant",
      description: "La dépression du moteur aspire l'air atmosphérique (21% O₂, 78% N₂) à travers le système"
    },
    {
      step: 2,
      title: "Passage dans le réacteur",
      description: "L'air traverse un réacteur céramique ou mécanique (tube en acier tressé) qui 'griffe' l'air et modifie son hygrométrie"
    },
    {
      step: 3,
      title: "Excitation moléculaire",
      description: "Les molécules d'O₂ et N₂ sont excitées, augmentant leur réactivité et leur capacité comburante"
    },
    {
      step: 4,
      title: "Mélange optimisé",
      description: "L'air 'augmenté' se mélange au carburant de manière plus homogène dans la chambre de combustion"
    },
    {
      step: 5,
      title: "Combustion améliorée",
      description: "Réaction chimique plus complète : CₙHₘ + O₂ (augmenté) → CO₂ + H₂O + Énergie (maximisée) + moins de polluants"
    }
  ];

  const comparisons = [
    {
      name: "Kit Ecopra",
      pros: [
        "Améliore la combustion sans modifier le moteur",
        "Compatible avec tous les moteurs thermiques, hybrides",
        "Compatible avec tous carburants (diesel, essence, GPL, GNV, hydrogène)",
        "Économique et écologique (15-35% économie)",
        "Installation aisée en 20-30 minutes (sans modification moteur)",
        "Durable et sans entretien (purement mécanique)",
        "Transposable de véhicule à véhicule",
        "Action scientifique sur O₂ et N₂",
        "Validé par cycle Artémis (norme européenne)"
      ],
      cons: [
        "Performances variables selon le moteur (15-35%)",
        "Installation en 50 minutes sans modification moteur"
      ],
      color: "green"
    },
    {
      name: "Reprogrammation moteur",
      pros: [
        "Augmente la puissance immédiatement",
        "Optimise certains paramètres moteur"
      ],
      cons: [
        "Peut endommager le moteur à long terme",
        "Annule parfois la garantie constructeur",
        "Modifie la cartographie électronique (irréversible)",
        "Pas légal sur route ouverte (sans passage DREAL)",
        "Assurance peut refuser de couvrir en cas d'accident",
        "Risque d'amende, immobilisation, voire retrait de carte grise",
        "Augmente la consommation de carburant",
        "Accélère l'usure mécanique"
      ],
      color: "red"
    },
    {
      name: "HHO (Oxyhydrogène)",
      pros: [
        "Peut améliorer légèrement la combustion",
        "Principe intéressant (électrolyse)"
      ],
      cons: [
        "Risque d'explosion majeur (mélange H₂ + O₂ très inflammable)",
        "Consommation électrique importante (surcharge alternateur)",
        "Installation potentiellement illégale (réglementation gaz)",
        "Entretien régulier nécessaire (électrolyte, électrodes)",
        "Usure prématurée de l'alternateur (-30% durée de vie)",
        "Efficacité contestée scientifiquement",
        "Nécessite électricité permanente",
        "Corrosion des composants"
      ],
      color: "orange"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-[#7CB342] to-[#00BCD4] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Technologie Ecopra
            </h1>
            <p className="text-xl md:text-2xl mb-4">
              Révolution'Air : Une Combustion Optimisée Sans Modifier le Moteur
            </p>
            <div className="inline-block bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full">
              <p className="text-lg">
                Innovation brevetée par <strong>{siteInfo.creator.name}</strong>
              </p>
              <p className="text-sm opacity-90 mt-1">
                {siteInfo.creator.since} • {siteInfo.creator.origin}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Scientific Principles */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              <span className="bg-gradient-to-r from-[#7CB342] to-[#00BCD4] bg-clip-text text-transparent">
                Principes Scientifiques
              </span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              {scientificPrinciples.map((principle, index) => {
                const IconComponent = principle.icon;
                const colorClass = principle.color === 'cyan' ? 'from-[#00BCD4] to-[#0097A7]' : 
                                   principle.color === 'blue' ? 'from-blue-500 to-blue-600' :
                                   'from-[#7CB342] to-[#689F38]';
                return (
                  <Card key={index} className="hover:shadow-xl transition-all">
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-4">
                        <div className={`p-4 bg-gradient-to-br ${colorClass} rounded-xl shadow-lg flex-shrink-0`}>
                          <IconComponent className="text-white" size={32} />
                        </div>
                        <div>
                          <h3 className="font-bold text-xl text-gray-900 mb-2">{principle.title}</h3>
                          <p className="text-gray-600 text-sm leading-relaxed">{principle.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <div className="bg-gradient-to-br from-[#7CB342]/10 to-[#00BCD4]/10 rounded-2xl p-8 border-2 border-[#00BCD4]/20">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Composition de l'air et son traitement</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-32 font-semibold text-gray-700">Air ambiant :</div>
                  <div className="flex-1 text-gray-600">78% Azote (N₂) + 21% Oxygène (O₂) + 1% Gaz divers</div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-32 font-semibold text-gray-700">Après Ecopra :</div>
                  <div className="flex-1 text-gray-600">"Air augmenté" = O₂ super réactif + N₂ structuré</div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-32 font-semibold text-gray-700">Résultat :</div>
                  <div className="flex-1 text-green-700 font-semibold">Combustion optimisée, plus complète et plus froide</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reaction Steps */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              <span className="bg-gradient-to-r from-[#7CB342] to-[#00BCD4] bg-clip-text text-transparent">
                Étapes de la Réaction Chimique
              </span>
            </h2>

            <div className="space-y-6">
              {reactionSteps.map((item, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#7CB342] to-[#00BCD4] rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                    <span className="text-white font-bold text-2xl">{item.step}</span>
                  </div>
                  <Card className="flex-1 border-l-4 border-[#00BCD4]">
                    <CardContent className="pt-4">
                      <h3 className="font-bold text-lg text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600 text-sm">{item.description}</p>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>

            <div className="mt-12 bg-gradient-to-r from-[#7CB342] to-[#00BCD4] text-white rounded-2xl p-8 shadow-xl">
              <h3 className="text-2xl font-bold mb-4 text-center">Équation simplifiée de combustion</h3>
              <div className="bg-white/20 backdrop-blur-sm rounded-lg p-6 text-center font-mono text-lg">
                <div className="mb-2">
                  CₙHₘ (carburant) + O₂ <span className="text-yellow-300 font-bold">(augmenté)</span> + N₂ <span className="text-yellow-300 font-bold">(structuré)</span>
                </div>
                <div className="text-3xl my-2">↓</div>
                <div>
                  CO₂ + H₂O + <span className="text-yellow-300 font-bold">Énergie maximisée</span> + <span className="text-green-300 font-bold">Moins de polluants</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Combustion Effects */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              <span className="bg-gradient-to-r from-[#7CB342] to-[#00BCD4] bg-clip-text text-transparent">
                Effets sur la Combustion
              </span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              {combustionEffects.map((effect, index) => (
                <Card key={index} className="border-2 border-[#00BCD4]/20 hover:border-[#00BCD4] transition-all">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <div className="inline-block px-6 py-3 bg-gradient-to-r from-[#7CB342] to-[#00BCD4] text-white rounded-full font-bold text-2xl mb-4">
                        {effect.value}
                      </div>
                      <h3 className="font-bold text-xl text-gray-900 mb-3">{effect.title}</h3>
                      <p className="text-gray-600 text-sm">{effect.description}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Technical Data Table */}
            <Card className="overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-[#7CB342] to-[#00BCD4] text-white">
                <CardTitle className="text-center text-2xl">Données Techniques Comparatives</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-6 py-4 text-left font-semibold text-gray-700">Paramètre</th>
                        <th className="px-6 py-4 text-center font-semibold text-gray-700">Sans Ecopra</th>
                        <th className="px-6 py-4 text-center font-semibold text-gray-700">Avec Ecopra</th>
                        <th className="px-6 py-4 text-center font-semibold text-green-700">Amélioration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {technicalData.map((row, index) => (
                        <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-6 py-4 font-medium text-gray-900">{row.label}</td>
                          <td className="px-6 py-4 text-center text-gray-600">{row.before}</td>
                          <td className="px-6 py-4 text-center text-gray-600">{row.after}</td>
                          <td className="px-6 py-4 text-center font-bold text-green-600">{row.improvement}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Validation scientifique */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              <span className="bg-gradient-to-r from-[#7CB342] to-[#00BCD4] bg-clip-text text-transparent">
                Validation Scientifique
              </span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-2 border-green-200 bg-green-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="text-green-600" />
                    Tests Cycle Artémis
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 text-sm mb-3">
                    Le cycle Artémis est la <strong>norme européenne de référence depuis 2015</strong> pour mesurer 
                    les performances et émissions des véhicules en conditions réelles.
                  </p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start gap-2">
                      <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-1" />
                      <span>Tests en conditions urbaines, routières et autoroutières</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-1" />
                      <span>Mesures précises des émissions polluantes</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-1" />
                      <span>Validation de l'économie de carburant</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 bg-blue-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Gauge className="text-blue-600" />
                    Tests Empiriques
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 text-sm mb-3">
                    Plus de <strong>10 000 installations</strong> réalisées en France et dans le monde depuis 2007.
                  </p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start gap-2">
                      <CheckCircle size={16} className="text-blue-600 flex-shrink-0 mt-1" />
                      <span>Retours clients positifs : 99% de satisfaction</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle size={16} className="text-blue-600 flex-shrink-0 mt-1" />
                      <span>Économies mesurées : 10 à 30% selon véhicules</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle size={16} className="text-blue-600 flex-shrink-0 mt-1" />
                      <span>Applications variées : auto, PL, agricole, industriel</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Comparatif détaillé */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
            Comparatif Technique Détaillé
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {comparisons.map((solution, index) => (
              <Card key={index} className={`${solution.color === 'green' ? 'border-4 border-[#7CB342] shadow-2xl' : ''}`}>
                <CardHeader className={`${solution.color === 'green' ? 'bg-gradient-to-r from-[#7CB342] to-[#00BCD4] text-white' : 'bg-gray-100'}`}>
                  <CardTitle className="text-center">
                    {solution.name}
                    {solution.color === 'green' && (
                      <Award className="inline ml-2" size={24} />
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-bold text-green-700 mb-2 flex items-center gap-2">
                        <CheckCircle size={20} />
                        Avantages
                      </h4>
                      <ul className="space-y-2">
                        {solution.pros.map((pro, i) => (
                          <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                            <span className="text-green-600 mt-1">✓</span>
                            <span>{pro}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-bold text-red-700 mb-2 flex items-center gap-2">
                        <X size={20} />
                        Inconvénients
                      </h4>
                      <ul className="space-y-2">
                        {solution.cons.map((con, i) => (
                          <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                            <span className="text-red-600 mt-1">✗</span>
                            <span>{con}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-[#7CB342] to-[#00BCD4] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            En Résumé
          </h2>
          <p className="text-xl mb-8 max-w-4xl mx-auto leading-relaxed">
            Le Kit Ecopra repose sur une technologie scientifiquement <strong>"DE RECHERCHES APPLIQUÉES"</strong> qui 
            modifie les propriétés comburantes de l'air (action sur O₂ et N₂) avant son entrée dans le moteur. 
            Contrairement aux solutions classiques, il n'exige <strong>aucune modification moteur ni entretien complexe</strong> et 
            permet des <strong>économies réelles mesurées (15-35%)</strong> tout en réduisant drastiquement l'impact écologique.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/commander"
              className="inline-block bg-white text-[#00BCD4] font-bold px-10 py-4 rounded-full hover:bg-gray-100 transition-all transform hover:scale-105 shadow-xl"
            >
              Commander maintenant
            </a>
            <a
              href="/contact"
              className="inline-block bg-transparent border-2 border-white text-white font-bold px-10 py-4 rounded-full hover:bg-white/10 transition-all"
            >
              Nous contacter
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Technologie;
